import storefront from "../global/storefront";
import { SWATCH_PRODUCTS_FOR_FABRIC_MATCH_QUERY } from "../global/storefront/queries";

const SWATCH_SEARCH_QUERY = "tag:'product_type:Swatch' OR tag:'Mega Swatches' OR tag:'Multi Swatch'";
const MAX_SWATCH_CANDIDATES = 250;

let swatchCandidatesPromise = null;

function numericIdFromGid(gid) {
  const match = typeof gid === "string" && gid.match(/(\d+)$/);

  return match ? match[1] : null;
}

function fetchSwatchCandidates() {
  if (!swatchCandidatesPromise) {
    swatchCandidatesPromise = storefront
      .request({
        query: SWATCH_PRODUCTS_FOR_FABRIC_MATCH_QUERY,
        variables: {
          query: SWATCH_SEARCH_QUERY,
          first: MAX_SWATCH_CANDIDATES,
        },
      })
      .then((response) => response?.data?.search?.edges?.map((edge) => edge.node).filter(Boolean) || [])
      .catch((error) => {
        console.error("[Cart Upsell] Failed to fetch swatch candidates:", error);

        return [];
      });
  }

  return swatchCandidatesPromise;
}

function buildUpsellCard(product) {
  const variantId = numericIdFromGid(product.variants?.nodes?.[0]?.id);

  if (!variantId) {
    return null;
  }

  const price = product.priceRange?.minVariantPrice;
  const productUrl = product.handle ? `/products/${product.handle}` : "#";

  const item = document.createElement("div");

  item.className = "cart-upsell__item";
  item.dataset.variantId = variantId;

  const productId = numericIdFromGid(product.id);

  if (productId) {
    item.dataset.productId = productId;
  }

  const imageLink = document.createElement("a");

  imageLink.href = productUrl;
  imageLink.className = "cart-upsell__image-link";
  imageLink.tabIndex = -1;

  const image = document.createElement("img");

  image.src = product.featuredImage?.url || "";
  image.alt = product.featuredImage?.altText || "";
  image.className = "cart-upsell__image";
  image.loading = "lazy";

  imageLink.append(image);

  const details = document.createElement("div");

  details.className = "cart-upsell__details";

  const header = document.createElement("div");

  header.className = "cart-upsell__header";

  const titleLink = document.createElement("a");

  titleLink.href = productUrl;
  titleLink.className = "cart-upsell__title";

  const heading = document.createElement("h3");

  heading.className = "h5";
  heading.textContent = product.title || "";

  titleLink.append(heading);

  const priceSnippet = document.createElement("price-snippet");

  priceSnippet.className = "cart-upsell__price";
  priceSnippet.setAttribute("dynamic", "");
  priceSnippet.setAttribute("price", String(price?.amount ?? 0));
  priceSnippet.setAttribute("compare-at-price", "0");
  priceSnippet.setAttribute("currency", price?.currencyCode || "USD");

  header.append(titleLink, priceSnippet);

  const addButton = document.createElement("button");

  addButton.type = "button";
  addButton.className = "button button--inverse cart-upsell__add";
  addButton.dataset.addUpsellToCart = "";
  addButton.dataset.variantId = variantId;
  addButton.textContent = window.localeStrings?.cart?.upsellAddToCart || "Add to Bag";

  details.append(header, addButton);
  item.append(imageLink, details);

  return item;
}

function resolveCartUpsell(script) {
  const container = script.closest("[data-cart-upsell]");
  const itemsEl = container?.querySelector("[data-cart-upsell-items]");
  const cartProductIdsScript = container?.querySelector("[data-cart-upsell-product-ids]");

  if (!container || !itemsEl) {
    return;
  }

  let fabricIds = [];
  let cartProductIds = [];

  try {
    fabricIds = JSON.parse(script.textContent);
  } catch {
    fabricIds = [];
  }

  try {
    cartProductIds = JSON.parse(cartProductIdsScript?.textContent || "[]").map(String);
  } catch {
    cartProductIds = [];
  }

  script.remove();
  cartProductIdsScript?.remove();

  if (!Array.isArray(fabricIds) || !fabricIds.length) {
    if (!itemsEl.children.length) {
      container.remove();
    }

    return;
  }

  fetchSwatchCandidates().then((candidates) => {
    const existingUpsellProductIds = new Set(
      [...itemsEl.querySelectorAll("[data-product-id]")].map((item) => item.dataset.productId).filter(Boolean),
    );

    fabricIds.forEach((fabricId) => {
      const match = candidates.find((candidate) => candidate.fabric?.reference?.id === fabricId);

      if (!match) {
        return;
      }

      const productId = numericIdFromGid(match.id);

      if (!productId) {
        return;
      }

      // Do not show products already in cart.
      if (cartProductIds.includes(String(productId))) {
        return;
      }

      // Do not show duplicate upsells already rendered.
      if (existingUpsellProductIds.has(String(productId))) {
        return;
      }

      const card = buildUpsellCard(match);

      if (!card) {
        return;
      }

      itemsEl.append(card);
      existingUpsellProductIds.add(String(productId));
    });

    if (!itemsEl.children.length) {
      container.remove();
    }
  });
}

function hydrateCartUpsells(root = document) {
  root.querySelectorAll("[data-cart-upsell-fabric-ids]").forEach(resolveCartUpsell);
}

hydrateCartUpsells();

document.addEventListener("updateCart", () => {
  hydrateCartUpsells();
});
